import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from "react";
import { Icon } from "./Icon.js";

/**
 * Portato da `components/overlay/Sheet.jsx` del design system.
 *
 * SOSTITUZIONE: l'originale usa `position: absolute` perché nei mockup vive
 * dentro il telaio di un telefono disegnato. In un'app vera deve coprire il
 * viewport, quindi qui è `fixed` — altrimenti si ancorerebbe al primo antenato
 * posizionato e apparirebbe nel posto sbagliato.
 *
 * Aggiunte rispetto all'originale: chiusura con Esc e blocco dello scorrimento
 * dietro al pannello, che su mobile è la differenza fra un foglio e un
 * fastidio.
 */
/** Oltre questa distanza il foglio si chiude, comunque lo si sia trascinato. */
const CLOSE_DISTANCE = 110;
/** Oppure oltre questa velocità: uno scatto breve e rapido vuol dire «via». */
const CLOSE_VELOCITY = 0.5;

const HEIGHTS = {
  auto: "auto",
  half: "54%",
  tall: "84%",
  full: "100%",
} as const;

export interface SheetProps {
  open: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: keyof typeof HEIGHTS;
  handle?: boolean;
  footer?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
}

export function Sheet({
  open,
  title,
  subtitle,
  size = "auto",
  handle = true,
  footer,
  onClose,
  children,
  style,
}: SheetProps) {
  // Il dialogo prende il nome dal proprio titolo: senza, uno screen reader
  // annuncia «finestra di dialogo» e basta.
  const titleId = useId();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  /**
   * Quanto il foglio è stato tirato giù col dito, in pixel.
   *
   * `null` = nessun trascinamento in corso, e il foglio torna a essere
   * governato dalla transizione CSS. Durante il gesto invece la transizione va
   * spenta: con essa attiva il pannello inseguirebbe il dito con un ritardo di
   * duecento millisecondi, che si sente come elastico rotto.
   */
  const [dragged, setDragged] = useState<number | null>(null);

  /** Dove e quando è cominciato il gesto. */
  const start = useRef({ y: 0, at: 0 });

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }

    setShown(false);
    // Resta montato finché l'animazione di uscita non è finita.
    const timer = setTimeout(() => setMounted(false), 240);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  /**
   * Trascinare il foglio verso il basso per chiuderlo.
   *
   * ── Le tre regole che lo fanno sembrare giusto ────────────────────────────
   *
   * **Solo verso il basso**: tirare in su non allunga il foglio, si ferma a
   * zero. Un pannello che si stira oltre la sua altezza è la firma di
   * un'animazione fatta a mano.
   *
   * **La soglia guarda anche la velocità**, non solo la distanza: uno scatto
   * breve e rapido chiude, un trascinamento lento e lungo che si ferma a metà
   * torna su. È così che si distingue «buttalo via» da «stavo guardando».
   *
   * **Il puntatore viene catturato**: senza, uscendo dal bordo del pannello il
   * gesto si interrompe a metà e il foglio resta in mezzo al nulla.
   */
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!onClose) return;

    // In un ref e non in una variabile locale: ogni movimento aggiorna lo
    // stato, quindi il componente si ridisegna — e una variabile del corpo
    // tornerebbe a zero a metà gesto, facendo saltare il foglio.
    start.current = { y: event.clientY, at: event.timeStamp };
    setDragged(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragged === null) return;

    setDragged(Math.max(0, event.clientY - start.current.y));
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (dragged === null) return;

    const distance = Math.max(0, event.clientY - start.current.y);
    const speed = distance / Math.max(1, event.timeStamp - start.current.at);

    setDragged(null);

    if (distance > CLOSE_DISTANCE || speed > CLOSE_VELOCITY) onClose?.();
  };

  if (!mounted) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        ...style,
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "var(--scrim)",
          opacity: shown ? 1 : 0,
          transition: "opacity var(--duration-base) var(--ease-standard)",
        }}
      />

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: 430,
          margin: "0 auto",
          background: "var(--surface-card)",
          borderRadius: "var(--radius-xl) var(--radius-xl) 0 0",
          height: HEIGHTS[size],
          maxHeight: "92dvh",
          transform:
            dragged !== null
              ? `translateY(${dragged}px)`
              : shown
                ? "translateY(0)"
                : "translateY(100%)",
          // Spenta mentre il dito è giù: deve seguirlo, non inseguirlo.
          transition: dragged === null ? "transform var(--duration-base) var(--ease-out)" : "none",
          // Il browser non deve interpretare il gesto come uno scorrimento
          // della pagina: senza, iOS se lo prende e il foglio non si muove.
          touchAction: "none",
        }}
      >
        {handle && (
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              display: "flex",
              justifyContent: "center",
              // L'area di presa è più alta della barretta che si vede: quattro
              // pixel sono un bersaglio impossibile per un pollice.
              padding: "10px 0 14px",
              flexShrink: 0,
              cursor: "grab",
            }}
          >
            <div
              style={{
                width: 38,
                height: 4,
                borderRadius: 999,
                background: "var(--border-strong)",
              }}
            />
          </div>
        )}

        {(title || onClose) && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "14px 20px 12px",
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && (
                <h2
                  id={titleId}
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    color: "var(--text-primary)",
                  }}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <div
                  style={{
                    fontFamily: "var(--font-text)",
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: "var(--text-secondary)",
                    marginTop: 3,
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>

            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Chiudi"
                style={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "var(--radius-pill)",
                  background: "var(--surface-sunken)",
                  color: "var(--text-primary)",
                }}
              >
                <Icon name="x" size={15} strokeWidth={2.2} />
              </button>
            )}
          </div>
        )}

        <div
          // La selezione del testo è spenta su tutta la cornice dell'app, ma
          // qui dentro no: un importo o la descrizione di una spesa uno li vuole
          // poter copiare, e negarlo sarebbe togliere una funzione per ottenere
          // un'estetica.
          className="qota-selectable"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: title ? "0 20px 20px" : "14px 20px 20px",
            // Lo scorrimento del foglio non deve propagarsi alla pagina sotto:
            // arrivati in fondo, senza questo, comincia a muoversi lo sfondo.
            overscrollBehavior: "contain",
          }}
        >
          {children}
        </div>

        {footer && (
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              gap: 10,
              // `max` e non somma: l'inset è lo spazio dell'indicatore home,
              // non un margine da aggiungere al proprio.
              padding: "14px 20px max(20px, env(safe-area-inset-bottom, 0px))",
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
